// v4 (V4-H2, spec-v4 5.1 and Q12): the channel switcher at the top of Home (and the channel's settings), shown when
// the account has two or more channels. A sheet lists them with their counts; "Add a channel" opens a new one's
// settings. The pick is remembered in localStorage (collections.ts writeCurrentChannel).
import { useState } from "react";
import { collections, writeCurrentChannel, type ChannelCard } from "../collections";
import { director } from "../director";
import { navigate } from "../router";
import { useStore } from "../store";
import type { Channel } from "../types";
import { Icon, Sheet, useLoad } from "./bits";

export const MAX_CHANNELS = 10;

/** `to` builds the link for a channel: its Home (/c/:id) or its settings. */
export function ChannelSwitcher({ current, to = (id) => `/c/${encodeURIComponent(id)}`, always = false }: { current: Channel; to?: (id: string) => string; always?: boolean }) {
  const channels = useStore((s) => s.me?.channels ?? []);
  const [open, setOpen] = useState(false);
  if (channels.length < 2 && !always) return null;
  return (
    <>
      <button type="button" className="chswitch" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-label={`Channel: ${current.name}. Switch channel`}>
        <span className="chswitch-dot" style={{ background: current.brand.colors.accent }} aria-hidden />
        <span className="chswitch-name">{current.name || "Your channel"}</span>
        <Icon name="chevron" size={16} />
      </button>
      {open && (
        <ChannelSheet
          currentId={current.id}
          onClose={() => setOpen(false)}
          onPick={(c) => {
            setOpen(false);
            writeCurrentChannel(c.id);
            director.context(`The user switched to their channel "${c.name}" by tapping.`);
            navigate(to(c.id));
          }}
        />
      )}
    </>
  );
}

function ChannelSheet({ currentId, onClose, onPick }: { currentId: string; onClose: () => void; onPick: (c: ChannelCard) => void }) {
  const known = useStore((s) => s.me?.channels ?? []);
  const { data } = useLoad(() => collections.channels(), []);
  // the counts arrive with GET /channels; until then the names from the session
  const list: (Channel & Partial<Pick<ChannelCard, "collectionCount" | "videoCount">>)[] = data ?? known;
  const full = list.length >= MAX_CHANNELS;
  return (
    <Sheet title="Your channels" onClose={onClose} className="chsheet">
      <div className="sheet-body">
        <ul className="chsheet-list">
          {list.map((c) => {
            const on = c.id === currentId;
            const counts = c.videoCount === undefined ? null : [c.collectionCount ? `${c.collectionCount} collection${c.collectionCount === 1 ? "" : "s"}` : null, `${c.videoCount} video${c.videoCount === 1 ? "" : "s"}`].filter(Boolean).join(" · ");
            return (
              <li key={c.id}>
                <button data-action="channel.switch" type="button" className={`chsheet-item${on ? " is-on" : ""}`} aria-current={on ? "true" : undefined} onClick={() => (on ? onClose() : onPick(c as ChannelCard))} data-autofocus={on ? true : undefined}>
                  <span className="chsheet-mark" style={{ background: c.brand.colors.bg, color: c.brand.colors.accent, borderColor: c.brand.colors.accent }} aria-hidden>
                    {(c.name || "C").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="chsheet-text">
                    <span className="chsheet-name">{c.name || "Your channel"}</span>
                    {counts && <span className="chsheet-meta">{counts}</span>}
                  </span>
                  {on && <Icon name="check" size={18} />}
                </button>
              </li>
            );
          })}
        </ul>
        <button
          data-action="channel.new"
          type="button"
          className="btn btn-quiet btn-block"
          disabled={full}
          onClick={() => {
            onClose();
            navigate("/c/new/settings");
          }}
        >
          <Icon name="plus" size={16} /> Add a channel
        </button>
        {full && <p className="sheet-note">You can have up to {MAX_CHANNELS} channels.</p>}
      </div>
    </Sheet>
  );
}
